import { useState, useMemo } from 'react';

import { useAppSelector } from 'hooks';

import { useAutoReformEnabled } from 'hooks/useAutoReformEnabled';

import {
  Box,
  Collapse,
  Button,
  Divider,
  Icon,
  Flex,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import type { APIPersonalAutoApplicationFormVehicle } from 'types/api.types';

import {
  PROVINCE_ALBERTA,
  PROVINCE_NEW_BRUNSWICK,
  PROVINCE_NOVA_SCOTIA,
} from 'variables/constants';

import AccidentBenefitsQuestions from 'views/auto_form/sections/Vehicles/components/AccidentBenefitsQuestions';

const formatAbReformLimit = (limit?: string | null) => {
  if (!limit || limit === 'Included') return 'Included';
  if (limit === 'Declined') return 'Declined';
  return `Limit: ${limit}`;
};

const Coverages = ({
  index,
  vehicle,
}: {
  index: number;
  vehicle: APIPersonalAutoApplicationFormVehicle;
}) => {
  const isAutoReformEnabled = useAutoReformEnabled();
  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isAlberta = province === PROVINCE_ALBERTA;
  const isNewBrunswick = province === PROVINCE_NEW_BRUNSWICK;
  const isNovaScotia = province === PROVINCE_NOVA_SCOTIA;

  const [isCollapsed, setIsCollapsed] = useState(!isAutoReformEnabled);

  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const insuranceCoverages = useAppSelector(
    (state) => state.autoPersonalForm.data.insurance_coverages,
  );

  const coverage = useMemo(() => {
    return insuranceCoverages.filter((_, idx) => idx === index)?.[0];
  }, [insuranceCoverages, index]);

  const bodilyInjuryLimit = useMemo(() => {
    const limit = coverage.liability.bodily_injury.limit;
    return (
      {
        '1000': '1M',
        '2000': '2M',
      }?.[limit] || limit
    );
  }, [coverage.liability.bodily_injury.limit]);

  if (!coverage) return null;

  return (
    <Box p="20px" py="10px" borderTop="1px solid" borderColor={borderColor}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="bold" fontSize="l">
          Coverages Overview
        </Text>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          rightIcon={
            <Icon as={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isCollapsed ? 'Show Coverages' : 'Hide Coverages'}
        </Button>
      </Flex>

      <Collapse in={!isCollapsed} animateOpacity>
        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          gap={4}
          mt={2}
        >
          <GridItem>
            <CoverageSection title="Liability">
              <CoverageItem
                label="Bodily Injury"
                value={coverage.liability.bodily_injury.premium}
                extra={`Limit: ${bodilyInjuryLimit}`}
              />
              <CoverageItem
                label="Property Damage"
                value={coverage.liability.property_damage.premium}
                extra={`Limit: ${bodilyInjuryLimit}`}
              />
            </CoverageSection>

            <CoverageSection title="Basic Coverage">
              <CoverageItem
                label="Accident Benefits"
                value={coverage.accident_benefits.premium}
              />
              <CoverageItem
                label="Uninsured Automobile"
                value={coverage.uninsured_automobile.premium}
              />
              <CoverageItem
                label="Direct Compensation"
                value={coverage.direct_compensation.premium}
                extra={`Deductible: ${
                  coverage.direct_compensation.deductible || '-'
                }`}
              />
            </CoverageSection>

            <CoverageSection title="Loss or Damage">
              <CoverageItem
                label="All Perils"
                value={coverage.loss_or_damage.all_perils.premium}
                extra={`Deductible: ${
                  coverage.loss_or_damage.all_perils.deductible || '-'
                }`}
              />
              <CoverageItem
                label="Collision or Upset"
                value={coverage.loss_or_damage.collision_or_upset.premium}
                extra={`Deductible: ${
                  coverage.loss_or_damage.collision_or_upset.deductible || '-'
                }`}
              />
              <CoverageItem
                label="Comprehensive"
                value={coverage.loss_or_damage.comprehensive.premium}
                extra={`Deductible: ${
                  coverage.loss_or_damage.comprehensive.deductible || '-'
                }`}
              />
              <CoverageItem
                label="Specified Perils"
                value={coverage.loss_or_damage.specified_perils.premium}
                extra={`Deductible: ${
                  coverage.loss_or_damage.specified_perils.deductible || '-'
                }`}
              />
            </CoverageSection>
          </GridItem>

          <GridItem>
            {isAutoReformEnabled ? (
              <>
                <CoverageSection title="Standard Accident Benefits">
                  <CoverageItem
                    label="Increased Medical, Rehabilitation & Attendant Care"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .medical_rehabilitation.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .medical_rehabilitation.limit,
                    )}
                  />
                </CoverageSection>

                <CoverageSection title="Optional Accident Benefits">
                  <CoverageItem
                    label="Optional Catastrophic Impairment"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .optional_catastrophic_impairment.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .optional_catastrophic_impairment.limit,
                    )}
                  />
                  <CoverageItem
                    label="Income Replacement"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .income_replacement.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .income_replacement.limit,
                    )}
                  />
                  <Divider my={2} />
                  <CoverageItem
                    label="Non-Earner"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .non_earner.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .non_earner.limit,
                    )}
                  />
                  <CoverageItem
                    label="Caregiver"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .caregiver.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .caregiver.limit,
                    )}
                  />
                  <Divider my={2} />
                  <CoverageItem
                    label="Lost Educational Expenses"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .lost_educational_expenses.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .lost_educational_expenses.limit,
                    )}
                  />
                  <CoverageItem
                    label="Expenses of Visitors"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .expenses_of_visitors.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .expenses_of_visitors.limit,
                    )}
                  />
                  <CoverageItem
                    label="Housekeeping & Home Maintenance"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .housekeeping_home_maintenance.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .housekeeping_home_maintenance.limit,
                    )}
                  />
                  <Divider my={2} />
                  <CoverageItem
                    label="Damage to Personal Items"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .damage_to_personal_items.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .damage_to_personal_items.limit,
                    )}
                  />
                  <CoverageItem
                    label="Death"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .death.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .death.limit,
                    )}
                  />
                  <CoverageItem
                    label="Funeral"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .funeral.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .funeral.limit,
                    )}
                  />
                  <CoverageItem
                    label="Dependant Care"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .dependant_care.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .dependant_care.limit,
                    )}
                  />
                  <CoverageItem
                    label="Indexation Benefit (Consumer Price Index)"
                    value={
                      coverage.ab_reform_optional_increased_accident_benefits
                        .indexation_benefit.premium
                    }
                    extra={formatAbReformLimit(
                      coverage.ab_reform_optional_increased_accident_benefits
                        .indexation_benefit.limit,
                    )}
                  />
                </CoverageSection>
              </>
            ) : (
              !isNewBrunswick &&
              !isNovaScotia &&
              !isAlberta && (
                <CoverageSection title="Optional Increased Accident Benefits">
                  <CoverageItem
                    label="Income Replacement"
                    value={
                      coverage.optional_increased_accident_benefits
                        .income_replacement.premium
                    }
                    extra={`Limit: ${
                      coverage.optional_increased_accident_benefits
                        .income_replacement.limit || '-'
                    }`}
                  />
                  <CoverageItem
                    label="Medical Rehabilitation"
                    value={
                      coverage.optional_increased_accident_benefits
                        .medical_rehabilitation.premium
                    }
                    extra={`Limit: ${
                      coverage.optional_increased_accident_benefits
                        .medical_rehabilitation.limit || '-'
                    }`}
                  />
                  <CoverageItem
                    label="Optional Catastrophic Impairment"
                    value={
                      coverage.optional_increased_accident_benefits
                        .optional_catastrophic_impairment
                    }
                  />
                  <CoverageItem
                    label="Caregiver, Housekeeping & Home Maintenance"
                    value={
                      coverage.optional_increased_accident_benefits
                        .caregiver_housekeeping_home_maintenance
                    }
                  />
                  <CoverageItem
                    label="Death & Funeral"
                    value={
                      coverage.optional_increased_accident_benefits
                        .death_funeral
                    }
                  />
                  <CoverageItem
                    label="Dependant Care"
                    value={
                      coverage.optional_increased_accident_benefits
                        .dependant_care
                    }
                  />
                  <CoverageItem
                    label="Indexation Benefit"
                    value={
                      coverage.optional_increased_accident_benefits
                        .indexation_benefit
                    }
                  />
                </CoverageSection>
              )
            )}

            <CoverageSection title="Policy Change Forms">
              <CoverageItem
                label="Family Protection Coverage"
                value={
                  coverage.policy_change_forms.family_protection_coverage
                    .premium
                }
              />
              {coverage.policy_change_forms.other_coverages.map(
                (otherCoverage, idx) => (
                  <CoverageItem
                    key={idx}
                    label={otherCoverage.name}
                    value={otherCoverage.premium}
                    extra={`Deductible/limit: ${
                      otherCoverage.deductible_limit || '-'
                    }`}
                  />
                ),
              )}
            </CoverageSection>
          </GridItem>
        </Grid>
      </Collapse>

      {isAutoReformEnabled && (
        <AccidentBenefitsQuestions vehicle={vehicle} index={index} />
      )}
    </Box>
  );
};

const CoverageSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box
      mb={4}
      borderRadius="md"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
    >
      <Box bg={bgColor} px={4} py={2} fontWeight="600">
        {title}
      </Box>
      <Box p={4}>{children}</Box>
    </Box>
  );
};

const CoverageItem = ({
  label,
  value,
  extra,
}: {
  label: string;
  value?: string;
  extra?: string;
}) => (
  <Flex justify="space-between" fontSize="sm" py={1}>
    <Text>{label}</Text>
    <Text fontWeight="500">
      {value || '-'}{' '}
      {extra && (
        <Text as="span" color="gray.500" ml={1}>
          ({extra})
        </Text>
      )}
    </Text>
  </Flex>
);

export default Coverages;
